import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { EditorView, createEditor } from '@annote/core/components/proseEditor';
import { getMarkdownFromView } from '@annote/core/components/proseEditor/utils';
import { parser } from '@annote/core/components/proseEditor/markdown';
import { Note } from '@annote/types';
import styled from 'styled-components';
import { text, colors } from '@annote/core/styles';
import { referenceNodeViewPlugin } from 'src/components/editor/referenceNodeViewPlugin';
import { Plugin, TextSelection } from 'prosemirror-state';
import { useHighlightedReferences } from 'src/context/HighlightedReferencesContext';

const EditorContainer = styled.div<{ disabled: boolean; placeholder: string; focusedPlaceholder: string }>`
  flex: 1;
  min-width: 0; /* Allow flex item to shrink below content size */

  .ProseMirror {
    width: 100%;
    padding: 10px 12px 10px 16px;
    font-family: ${text.family.primary};
    font-size: ${text.size.primary};
    color: ${colors.text.primary};
    background: transparent;
    border: none;
    outline: none;
    min-height: 20px;
    overflow: hidden;
    line-height: 20px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    box-sizing: border-box;
    cursor: text;

    &::placeholder {
      color: ${colors.text.placeholder};
      text-align: left;
    }

    ${({ disabled }) =>
      disabled &&
      `
      opacity: 0.6;
      cursor: not-allowed;
    `}

    /* Style for reference marks */
    .prosemirror-reference {
      background: ${colors.bg.quote.primary};
      padding: 2px 4px;
      border-radius: 3px;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: ${colors.bg.quote.hover};
      }
    }

    /* Placeholder styling - matching ProseEditor approach */
    p:first-child:last-child:has(br:only-child) {
      position: relative;
    }

    p:first-child:last-child:has(br:only-child)::after {
      content: ${({ placeholder }) => `"${placeholder}"`};
      font-family: ${text.family.primary};
      font-size: ${text.size.primary};
      font-weight: ${text.weight.regular};
      color: ${colors.text.placeholder};
      pointer-events: none;
      position: absolute;
      left: 0;
      top: 0;
    }

    /* Show focused placeholder with cursor indicator when focused */
    &.ProseMirror-focused p:first-child:last-child:has(br:only-child)::after {
      content: ${({ focusedPlaceholder }) => `"${focusedPlaceholder}"`};
    }

    p {
      margin: 0;
      padding: 0;
      line-height: 20px;
      display: inline;
      white-space: nowrap;
      min-height: 20px; /* Ensure paragraph has height for cursor visibility */
    }

    /* Make reference nodes inline and compact */
    .prosemirror-reference-node-view {
      display: inline-flex !important;
      vertical-align: middle !important;
      flex-shrink: 0;
    }

    /* Hide any fallback text content in reference nodes */
    .prosemirror-reference-node {
      display: inline-flex !important;
      vertical-align: middle !important;

      /* Hide any text content that might leak through */
      font-size: 0;
      line-height: 0;
      color: transparent;

      /* Only show the NodeView content */
      > * {
        font-size: ${text.size.small};
        line-height: normal;
        color: ${colors.text.primary};
      }
    }
  }
`;

export interface ChatInputHandle {
  getValue: () => string;
  setValue: (value: string) => void;
  focus: () => void;
  blur: () => void;
}

interface ChatInputProps {
  placeholder?: string;
  focusedPlaceholder?: string;
  disabled?: boolean;
  onSubmit?: (value: string) => void;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      placeholder = 'Ask Anni to summarize, write, organize, review, etc...',
      focusedPlaceholder = '| organize, summarize, review, find...',
      disabled = false,
      onSubmit,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const editorViewRef = useRef<EditorView | null>(null);
    const { setHighlightedReferences } = useHighlightedReferences();

    // Initialize editor
    useEffect(() => {
      if (!containerRef.current || editorViewRef.current) return undefined;

      // Create a temporary note for the editor
      const tempNote: Note = {
        id: 'chat-input-temp' as Note['id'],
        type: 'takeaway',
        title: '',
        value: '',
        createdAt: new Date().toISOString(),
        links: [],
        backlinks: [],
      };

      editorViewRef.current = createEditor(
        containerRef.current,
        tempNote,
        {
          plugins: [referenceNodeViewPlugin()] as Plugin[],
          handleKeyDown: (view, event) => {
            // Handle Enter key for submission
            if (event.key === 'Enter' && !event.shiftKey && onSubmit && !disabled) {
              event.preventDefault();
              const value = getMarkdownFromView(view);
              if (value.trim()) {
                onSubmit(value);
              }
              return true; // Event handled
            }
            return false; // Not handled, let ProseMirror process it
          },
        },
        false, // readOnly
        false, // enableSuggestions
        true, // spellCheck
      );

      // Set contenteditable and aria attributes on the ProseMirror element
      const proseMirrorEl = containerRef.current.querySelector('.ProseMirror');
      if (proseMirrorEl) {
        const element = proseMirrorEl as HTMLElement;
        element.setAttribute('contenteditable', !disabled ? 'true' : 'false');
        element.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      // Fix for Chrome not showing cursor in empty ProseMirror editors
      // When user clicks into empty editor, explicitly set selection to make cursor visible
      const handleFocus = (): void => {
        if (!editorViewRef.current) return;
        const { state } = editorViewRef.current;
        const isEmpty =
          state.doc.content.size === 0 || (state.doc.content.size === 2 && state.doc.firstChild?.isTextblock);
        if (isEmpty) {
          const { tr } = state;
          const selection = TextSelection.create(tr.doc, 1);
          tr.setSelection(selection);
          editorViewRef.current.dispatch(tr);
        }
      };

      proseMirrorEl?.addEventListener('focus', handleFocus);

      return () => {
        proseMirrorEl?.removeEventListener('focus', handleFocus);
        if (editorViewRef.current) {
          editorViewRef.current.destroy();
          editorViewRef.current = null;
        }
      };
    }, [onSubmit, disabled, placeholder]);

    // Clear highlights when chat input is cleared/unmounted
    useEffect(() => {
      return () => {
        setHighlightedReferences([]); // Clear highlights when unmounting
      };
    }, [setHighlightedReferences]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getValue: (): string => {
        if (!editorViewRef.current) return '';
        return getMarkdownFromView(editorViewRef.current);
      },
      setValue: (value: string): void => {
        if (!editorViewRef.current) return;
        const { tr } = editorViewRef.current.state;
        const doc = parser.parse(value);
        tr.replaceWith(0, editorViewRef.current.state.doc.content.size, doc.content);
        editorViewRef.current.dispatch(tr);
      },
      focus: (): void => {
        if (editorViewRef.current && !disabled) {
          editorViewRef.current.focus();
          // Ensure cursor is visible in empty editor by setting a selection
          // Chrome doesn't show cursor in empty ProseMirror editors without explicit selection
          const { state } = editorViewRef.current;
          const isEmpty =
            state.doc.content.size === 0 || (state.doc.content.size === 1 && state.doc.firstChild?.nodeSize === 2);
          if (isEmpty) {
            // Empty or only contains empty paragraph - set selection at start
            const { tr } = state;
            // For empty docs, position 1 is after the doc node, before the first paragraph
            // Position 2 is inside the empty paragraph (where cursor should be)
            const pos = tr.doc.content.size === 0 ? 1 : 2;
            const selection = TextSelection.near(tr.doc.resolve(pos));
            tr.setSelection(selection);
            editorViewRef.current.dispatch(tr);
          }
        }
      },
      blur: (): void => {
        if (editorViewRef.current) {
          editorViewRef.current.dom.blur();
        }
      },
    }));

    // Update disabled state
    useEffect(() => {
      if (containerRef.current) {
        const proseMirror = containerRef.current.querySelector('.ProseMirror');
        if (proseMirror) {
          const element = proseMirror as HTMLElement;
          element.setAttribute('contenteditable', !disabled ? 'true' : 'false');
          element.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        }
      }
    }, [disabled]);

    return (
      <EditorContainer
        ref={containerRef}
        disabled={disabled}
        placeholder={placeholder}
        focusedPlaceholder={focusedPlaceholder}
        data-testid="annie-chat-input"
      />
    );
  },
);

ChatInput.displayName = 'ChatInput';
